import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { COMMUNICATION_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const BASE_TOPICS = `${COMMUNICATION_SERVICE_BASE_URL}/api/v1/notification-topics`;
const BASE_RELATIONS = `${COMMUNICATION_SERVICE_BASE_URL}/api/v1/topics`;

export default function useNotificationTopics() {
  const authToken = sessionStorage.getItem("authToken");

  const [notificationTopics, setNotificationTopics] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  // error handler
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  // fetch all topics
  const fetchAllTopics = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);

    try {
      const res = await apiClient.get(
        `${BASE_TOPICS}?page=${page}&size=${pageSize}`,
      );

      const topics = Array.isArray(res.data?.content) ? res.data.content : [];

      setNotificationTopics(topics.map(formatTopicData));
      setTotalPages(res.data?.totalPages || 1);
      setTotalElements(res.data?.totalElements || topics.length);
    } catch (error) {
      handleAxiosErrors(error, "fetch topics");
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [authToken, page, pageSize]);

  // fetch by id
  const fetchTopicById = useCallback(
    async (topicId) => {
      if (!authToken) return;
      setLoading(true);

      try {
        const res = await apiClient.get(`${BASE_TOPICS}/${topicId}`);
        return formatTopicData(res.data);
      } catch (error) {
        handleAxiosErrors(error, "fetch topic");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // create
  const createTopic = useCallback(
    async (payload) => {
      if (!authToken) return;
      try {
        const res = await apiClient.post(BASE_TOPICS, payload);

        setNotificationTopics((prev) => [formatTopicData(res.data), ...prev]);

        toast.success("Topic created successfully");
        return res;
      } catch (error) {
        handleAxiosErrors(error, "create topic");
      }
    },
    [authToken],
  );

  // update
  const updateTopic = useCallback(
    async (payload, topicId) => {
      if (!authToken) return;
      try {
        const res = await apiClient.patch(`${BASE_TOPICS}/${topicId}`, payload);
        fetchAllTopics();
        toast.success("Topic updated successfully");
      } catch (error) {
        handleAxiosErrors(error, "update topic");
      }
    },
    [authToken],
  );

  // delete
  const deleteTopic = useCallback(
    async (topicId) => {
      if (!authToken) return;
      try {
        await apiClient.delete(`${BASE_TOPICS}/${topicId}`);

        setNotificationTopics((prev) => prev.filter((t) => t.id !== topicId));

        toast.success("Topic deleted successfully");
      } catch (error) {
        handleAxiosErrors(error, "delete topic");
      }
    },
    [authToken],
  );

  // toggle enable/disable
  const toggleTopic = useCallback(
    async (topicId, enabled) => {
      if (!authToken) return;

      try {
        const res = await apiClient.patch(`${BASE_TOPICS}/${topicId}`, [
          { op: "replace", path: "/enabled", value: enabled },
        ]);

        setNotificationTopics((prev) =>
          prev.map((t) => (t.id === topicId ? formatTopicData(res.data) : t)),
        );

        toast.success("Topic updated");
      } catch (error) {
        handleAxiosErrors(error, "toggle topic");
      }
    },
    [authToken],
  );

  // bulk assign user to topic
  const bulkAssignUsersToTopic = useCallback(
    async (topicId, payload) => {
      if (!authToken) return;

      try {
        await apiClient.post(
          `${BASE_RELATIONS}/${topicId}/users/bulk`,
          payload,
        );
      } catch (error) {
        handleAxiosErrors(error, "assign user to topic");
      }
    },
    [authToken],
  );

  const removeUserFromTopic = useCallback(
    async (topicId, userId) => {
      if (!authToken) return;

      try {
        await apiClient.delete(`${BASE_RELATIONS}/${topicId}/users/${userId}`);

        setNotificationTopics((prev) =>
          prev.map((topic) =>
            topic.id === topicId
              ? {
                  ...topic,
                  users: topic.users.filter((u) => u.id !== userId),
                }
              : topic,
          ),
        );

        toast.success("User removed from topic");
      } catch (error) {
        handleAxiosErrors(error, "remove user from topic");
      }
    },
    [authToken],
  );

  const bulkAssignRolesToTopic = useCallback(
    async (topicId, payload) => {
      if (!authToken) return;

      try {
        await apiClient.post(
          `${BASE_RELATIONS}/${topicId}/roles/bulk`,
          payload,
        );
      } catch (error) {
        handleAxiosErrors(error, "assign roles to topic");
      }
    },
    [authToken],
  );

  const removeRoleFromTopic = useCallback(
    async (topicId, roleId) => {
      if (!authToken) return;

      try {
        await apiClient.delete(`${BASE_RELATIONS}/${topicId}/roles/${roleId}`);

        setNotificationTopics((prev) =>
          prev.map((topic) =>
            topic.id === topicId
              ? {
                  ...topic,
                  roles: topic.roles.filter((r) => r.id !== roleId),
                }
              : topic,
          ),
        );

        toast.success("Role removed from topic");
      } catch (error) {
        handleAxiosErrors(error, "remove role from topic");
      }
    },
    [authToken],
  );

  // handle channels
  const addChannelToTopic = useCallback(
    async (topicId, channel) => {
      if (!authToken) return;

      try {
        const res = await apiClient.post(
          `${BASE_RELATIONS}/${topicId}/channels`,
          channel,
        );

        setNotificationTopics((prev) =>
          prev.map((topic) =>
            topic.id === topicId
              ? {
                  ...topic,
                  channels: [...(topic.channels || []), channel],
                }
              : topic,
          ),
        );

        toast.success("Channel added to topic");
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "add channel to topic");
      }
    },
    [authToken],
  );

  const removeChannelFromTopic = useCallback(
    async (topicId, channelId) => {
      if (!authToken) return;

      try {
        await apiClient.delete(
          `${BASE_RELATIONS}/${topicId}/channels/${channelId}`,
        );

        setNotificationTopics((prev) =>
          prev.map((topic) =>
            topic.id === topicId
              ? {
                  ...topic,
                  channels: topic.channels.filter((c) => c.id !== channelId),
                }
              : topic,
          ),
        );
      } catch (error) {
        handleAxiosErrors(error, "remove channel");
      }
    },
    [authToken],
  );

  const assignTemplateToTopic = async (topicId, templateId) => {
    try {
      const res = await apiClient.patch(
        `${BASE_TOPICS}/${topicId}/template/${templateId}`,
      );

      fetchAllTopics();

      return res.data;
    } catch (error) {
      handleAxiosErrors(error, "Assign template failed");
    }
  };

  const removeTemplateFromTopic = async (topicId, templateId) => {
    try {
      const res = await apiClient.delete(
        `${BASE_TOPICS}/${topicId}/template/${templateId}`,
      );

      fetchAllTopics();
      toast.success("Template removed from topic");
      return res.data;
    } catch (error) {
      handleAxiosErrors(error, "remove template failed");
    }
  };

  useEffect(() => {
    if (authToken) fetchAllTopics();
  }, [authToken, fetchAllTopics]);

  return {
    notificationTopics,
    loading,
    initialLoad,
    fetchAllTopics,
    fetchTopicById,
    createTopic,
    updateTopic,
    deleteTopic,
    toggleTopic,
    removeUserFromTopic,
    bulkAssignRolesToTopic,
    bulkAssignUsersToTopic,
    removeRoleFromTopic,
    addChannelToTopic,
    removeChannelFromTopic,
    assignTemplateToTopic,
    removeTemplateFromTopic,
    totalPages,
    totalElements,
    page,
    pageSize,
    setPage,
    setPageSize,
  };
}

// formatter
const formatTopicData = (topic = {}) => {
  return {
    id: topic.id || null,
    name: topic.name || "",
    description: topic.description || "",
    enabled: topic.enabled === true,
    channels: Array.isArray(topic.channels)
      ? topic.channels.map((c) => ({
          id: c.id,
          type: c.type,
          description: c.description,
        }))
      : [],
    roles: Array.isArray(topic.roleRecipients)
      ? topic.roleRecipients.map((role) => ({
          id: role?.roleId,
          name: role?.roleName,
          description: role.description || "",
        }))
      : [],
    users: Array.isArray(topic.userRecipients)
      ? topic.userRecipients.map((u) => ({
          id: u?.userId,
          username: u?.username,
          email: u?.email,
          firstName: u?.firstName,
          lastName: u?.lastName,
          phone: u?.phone,
          description: u.description || "",
        }))
      : [],
    template: topic.messageTemplate
      ? {
          id: topic.messageTemplate.id,
          code: topic.messageTemplate.code,
        }
      : null,
    createdAt: topic.createdAt || null,
    updatedAt: topic.updatedAt || null,
    // ...topic,
  };
};
